<template>
  <main class="page-shell">
    <section class="section">
      <div class="container">
        <article v-if="study" class="surface details fade-up">
          <router-link to="/" class="back-link">Back to Portfolio</router-link>
          <p class="eyebrow">Case Study</p>
          <h1>{{ study.title }}</h1>
          <p class="overview">{{ study.overview }}</p>
          <p class="summary">{{ study.summary }}</p>

          <div class="content-grid">
            <section>
              <h2>Business Challenge</h2>
              <p>{{ study.businessChallenge }}</p>
            </section>
            <section>
              <h2>Solution</h2>
              <p>{{ study.solution }}</p>
            </section>
            <section>
              <h2>Responsibilities</h2>
              <ul>
                <li v-for="item in study.responsibilities" :key="item">{{ item }}</li>
              </ul>
            </section>
            <section>
              <h2>Outcome</h2>
              <ul>
                <li v-for="item in study.outcome" :key="item">{{ item }}</li>
              </ul>
            </section>
          </div>

          <section class="role-block">
            <h2>Architecture Overview</h2>
            <div class="architecture-diagram" aria-label="Architecture diagram">
              <div class="node">{{ study.architecture.frontend }}</div>
              <span class="arrow" aria-hidden="true">↓</span>
              <div class="node">{{ study.architecture.api }}</div>
              <span class="arrow" aria-hidden="true">↓</span>
              <div class="node">{{ study.architecture.database }}</div>
            </div>
          </section>

          <section class="role-block">
            <h2>Technology</h2>
            <ul>
              <li v-for="tech in study.technology" :key="tech">{{ tech }}</li>
            </ul>
          </section>

          <section v-if="study.keyFeatures && study.keyFeatures.length" class="role-block">
            <h2>Key Features</h2>
            <div class="feature-grid">
              <article v-for="feature in study.keyFeatures" :key="feature" class="feature-card">
                <p>{{ feature }}</p>
              </article>
            </div>
          </section>

          <section v-if="study.engineeringHighlights && study.engineeringHighlights.length" class="role-block">
            <h2>Engineering Highlights</h2>
            <ul>
              <li v-for="item in study.engineeringHighlights" :key="item">{{ item }}</li>
            </ul>
          </section>

          <section class="role-block">
            <h2>Screenshots</h2>
            <div class="screenshot-grid">
              <figure v-for="(shot, index) in study.screenshots" :key="`${study.slug}-${index}`">
                <img :src="shot.src" :alt="shot.alt" loading="lazy" decoding="async" />
              </figure>
            </div>
          </section>

          <section class="role-block">
            <h2>Lessons Learned</h2>
            <ul>
              <li v-for="item in study.lessonsLearned" :key="item">{{ item }}</li>
            </ul>
          </section>

          <section v-if="study.futureEnhancements && study.futureEnhancements.length" class="role-block">
            <h2>Future Enhancements</h2>
            <ul>
              <li v-for="item in study.futureEnhancements" :key="item">{{ item }}</li>
            </ul>
          </section>

          <section class="role-block">
            <h2>Links</h2>
            <div class="links-row">
              <a v-if="study.githubUrl" :href="study.githubUrl" target="_blank" rel="noopener noreferrer">GitHub Repository</a>
              <span v-else>GitHub repository available on request for non-public work.</span>
              <a v-if="study.liveDemoUrl" :href="study.liveDemoUrl" target="_blank" rel="noopener noreferrer">Live Demo</a>
            </div>
            <p class="timeline">
              Repository documentation standard: business context, architecture, setup, deployment notes, and operational considerations.
            </p>
          </section>

          <p class="subtle-cta">{{ caseCta }}</p>

          <div class="cta-row">
            <a :href="profile.scheduleCallUrl" class="primary" target="_blank" rel="noopener noreferrer">
              Book a Discovery Call
            </a>
            <a :href="`mailto:${profile.email}`" class="secondary">Discuss Your Project</a>
          </div>
        </article>

        <article v-else class="surface details fade-up">
          <h1>Case Study Not Found</h1>
          <p>The requested case study is unavailable.</p>
          <router-link to="/" class="back-link">Return Home</router-link>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import { caseStudies, profile } from "@/data/portfolioContent";

export default {
  name: "CaseStudyPage",
  computed: {
    profile() {
      return profile;
    },
    study() {
      return caseStudies.find((item) => item.slug === this.$route.params.slug);
    },
    caseCta() {
      if (this.study && this.study.slug === "kineticore-gym-app") {
        return "Interested in building a scalable mobile application? Let's discuss your project.";
      }
      return "Planning a production system with clear ownership from discovery to support? Let's discuss your project.";
    }
  },
  mounted() {
    if (this.study) {
      document.title = `${this.study.title} | Jerald Pamugas`;
    }
  }
};
</script>

<style scoped>
.details {
  padding: clamp(1.4rem, 3vw, 2.4rem);
}

.back-link {
  text-decoration: none;
  color: var(--text-secondary);
  border-bottom: 1px solid transparent;
}

.back-link:hover,
.back-link:focus-visible {
  border-bottom-color: var(--accent);
  color: var(--accent);
}

.eyebrow {
  margin: 1rem 0 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  font-size: 0.8rem;
}

h1 {
  margin: 0.5rem 0 1rem;
  font-family: var(--font-display);
  font-size: clamp(1.9rem, 4.4vw, 3rem);
  line-height: 1.2;
}

.overview {
  margin: 0;
  color: var(--text-primary);
  line-height: 1.65;
  font-weight: 600;
  max-width: 860px;
}

.summary {
  margin: 0.7rem 0 0;
  color: var(--text-secondary);
  line-height: 1.75;
  max-width: 800px;
}

.content-grid {
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.1rem;
}

.content-grid section {
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.018);
}

h2 {
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
}

p,
li {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.75;
}

ul {
  margin: 0;
  padding-left: 1.2rem;
}

.role-block {
  margin-top: 1.1rem;
  border-top: 1px solid var(--border-color);
  padding-top: 1.2rem;
}

.architecture-diagram {
  display: grid;
  justify-items: center;
  gap: 0.45rem;
}

.node {
  width: min(420px, 100%);
  text-align: center;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  color: var(--text-secondary);
}

.arrow {
  color: var(--text-muted);
}

.screenshot-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

figure {
  margin: 0;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}

img {
  display: block;
  width: 100%;
  max-height: 640px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.02);
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
}

.feature-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.015);
}

.feature-card p {
  margin: 0;
}

.links-row {
  display: grid;
  gap: 0.55rem;
}

.links-row a,
.links-row span {
  color: var(--text-secondary);
}

.timeline {
  color: var(--text-muted);
  margin-top: 0.7rem;
}

.subtle-cta {
  margin: 1.3rem 0 0;
  color: var(--text-secondary);
  line-height: 1.7;
}

.cta-row {
  margin-top: 1.6rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.primary,
.secondary {
  text-decoration: none;
  border-radius: 999px;
  padding: 0.72rem 1.2rem;
  font-weight: 600;
}

.primary {
  background: var(--accent-strong);
  color: #05111b;
}

.secondary {
  border: 1px solid var(--border-color);
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .screenshot-grid {
    grid-template-columns: 1fr;
  }

  .feature-grid {
    grid-template-columns: 1fr;
  }
}
</style>
